/**
 * Rerun prefill: reopen a report's configuration dialog on the parameters the
 * report on screen was actually generated with.
 *
 * The single source is the window's stored `requestParams` - the payload
 * ReportWindowsContext threads onto every ready window, whether the report was
 * just generated from the Reports page, re-run from the Saved/Recent rail
 * (lib/generateReport.runSavedReportGeneration), opened by the chat handoff, or
 * produced by a previous rerun. There is no parallel "thin params" channel to
 * keep in sync, and a report opened from history prefills exactly like one
 * generated a second ago.
 *
 * `requestParams` is the output of buildReportRequestParams, so every option key
 * already matches the name the dialog submits (`currency`, `irr_method`,
 * `projection_months`, ...) and reads back with `prefilled()` unchanged. Only the
 * portfolio SELECTION changes shape on the way out - the dialog holds an item
 * picked from the /codenames list, not a codename string - which is what
 * `selectionFromParams` reconstructs for PortfolioSelector.
 *
 * Round-tripping is stable: a value that came out of buildReportRequestParams
 * goes back in unchanged (capToMaxDate/snapToBusinessDay are idempotent on an
 * already-resolved date), so opening the dialog and pressing OK regenerates the
 * same report.
 */

// One descriptor per requestParams object. Dialogs call selectionFromParams
// inline in JSX, and PortfolioSelector's restore effect depends on the result -
// a fresh object per render would re-run that effect on every keystroke.
const descriptors = new WeakMap();

/**
 * The exact prior portfolio selection, in the shape PortfolioSelector's
 * `initialSelection` prop restores - or null when the report carried no
 * portfolio pick (a client-context report, whose scope rides on client_id).
 *
 * Referentially stable for a given `requestParams` object, so it is safe to
 * call in render.
 */
export function selectionFromParams(requestParams) {
  if (!requestParams || typeof requestParams !== 'object') return null;
  if (!descriptors.has(requestParams)) {
    descriptors.set(requestParams, buildSelection(requestParams));
  }
  return descriptors.get(requestParams);
}

function buildSelection(p) {
  if (p.group_codename) {
    return {
      group_codename: p.group_codename,
      // @@ aggregate vs @ per-account. Absent means the sequential default,
      // matching buildSelectionParams.
      group_mode: p.group_mode === 'aggregate' ? 'aggregate' : 'sequential',
      // Only present when the PM narrowed a group to one member ('ALL' is
      // never sent).
      specific_account: p.specific_account || 'ALL',
    };
  }
  if (p.account_number) {
    return { account_number: String(p.account_number) };
  }
  if (p.client_codename) {
    // Legacy/handoff shape: a codename with no account number. Resolved by
    // name rather than identity, so a codename owning several accounts stays
    // ambiguous and the PM picks (never silently one of several).
    return { client_codename: p.client_codename };
  }
  return null;
}

/**
 * A stored option, or the dialog's own default when the report did not carry it.
 * null and undefined both count as absent; `false` and `0` are real values.
 */
export function prefilled(requestParams, key, fallback) {
  const value = requestParams ? requestParams[key] : undefined;
  return value === undefined || value === null ? fallback : value;
}

/**
 * The stored { start, end } window, or the dialog's default range. Each end is
 * taken independently so a half-filled stored range still contributes.
 */
export function prefilledRange(requestParams, fallback) {
  const stored = (requestParams && requestParams.date_range) || {};
  return {
    start: stored.start || fallback.start,
    end: stored.end || fallback.end,
  };
}
